import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ViewerFooter.css';
import { device } from "../../../../utils/helpers";
import { useSelector, useDispatch } from 'react-redux';
import { DemoImageData, deleteImage } from '../../../../store/features/imagesDataSlice';
import { updateState } from '../../../../store/features/generalStatesSlice';
import { DivRef } from '../../../../utils/typesAndMaps';
import { RootStore } from '../../../../store';
import { updateStyles } from '../../../../store/features/nodeStylesSlice';

interface ViewerFooterProps {
  panelRef: DivRef,
}

export default function ViewerFooter({ panelRef }: ViewerFooterProps) {
  const [isDeleteMode, setDeleteMode] = useState(false);
  const [isHideFooter, setFooterVisibility] = useState(false);

  const imagesData = useSelector((store: RootStore) => store.imagesData);
  const generalStates = useSelector((store: RootStore) => store.generalStates);
  const nodeStyles = useSelector((store: RootStore) => store.nodeStyles);

  const imageDataRef = useRef(imagesData);
  const isDeleteModeRef = useRef(isDeleteMode);
  const currentImageIndexRef = useRef(generalStates.curImgIdx);
  const nodeStylesRef = useRef(nodeStyles);

  const dispatch = useDispatch();

  useEffect(() => {
    imageDataRef.current = imagesData;
    currentImageIndexRef.current = generalStates.curImgIdx;
    isDeleteModeRef.current = isDeleteMode;
    nodeStylesRef.current = nodeStyles;
  }, [imagesData, generalStates.curImgIdx, isDeleteMode, nodeStyles])

  useEffect(() => {
    const resizeEvent = () => {
      let _viewMainStyle = {
        ...nodeStylesRef.current.viewerMainStyles,
        height: isHideFooter ? "calc(100% - 70px)" : "calc(100% - 190px)"
      }
      if (device() === "mobile") {
        _viewMainStyle.height = "100%";
      } else if (device() === "ipad") {
        _viewMainStyle.height = "calc(100% - 170px)";
      } else {
        _viewMainStyle.height = !isHideFooter ? "calc(100% - 190px)" : "calc(100% - 80px)";
      }
      dispatch(updateStyles({ viewerMainStyles: _viewMainStyle }));
    }
    const resizeFooterToggle = () => {
      device() === "pc" && isHideFooter && setFooterVisibility(false);
    }
    resizeEvent();
    window.addEventListener("resize", resizeEvent);
    window.addEventListener("resize", resizeFooterToggle);
    return () => {
      window.removeEventListener("resize", resizeEvent);
      window.removeEventListener("resize", resizeFooterToggle);
    }
  }, [isHideFooter, dispatch])

  const handleCurrentImage = useCallback(async (index: number, isAddImage?: boolean) => {
    if (!isDeleteModeRef.current || isAddImage) {
      dispatch(updateStyles({ fileUploadArea: { borderColor: nodeStylesRef.current.fileUploadArea.borderColor, display: "none" } }));
      dispatch(updateState({ stateName: "curImgIdx", value: index }));
    } else {
      if (currentImageIndexRef.current !== -1) {
        let idx = index;
        if (index === currentImageIndexRef.current) {
          idx = index - 1;
        } else if (index > currentImageIndexRef.current) {
          idx = currentImageIndexRef.current
        } else if (index < currentImageIndexRef.current) {
          idx = currentImageIndexRef.current - 1;
        }
        dispatch(updateState({ stateName: "curImgIdx", value: idx }))
      }
      dispatch(deleteImage({ index }));
    }
  }, [dispatch])

  useEffect(() => {
    const isHasUploadImage = !!(imageDataRef.current.find((img: DemoImageData) => { return !img.sampleImg }));
    if (imageDataRef.current.length > 5 || isHasUploadImage) {
      handleCurrentImage(imageDataRef.current.length - 1, true);
    }
  }, [imagesData.length, handleCurrentImage])

  useEffect(() => {
    if (panelRef.current!.children[generalStates.curImgIdx]) {
      const panel = panelRef.current!;
      panel.scrollLeft = (panel.children[generalStates.curImgIdx] as HTMLDivElement).offsetLeft - panel.clientWidth / 2;
      panel.scrollTop = (panel.children[generalStates.curImgIdx] as HTMLDivElement).offsetTop - panel.clientHeight / 2;
    }
  }, [generalStates.curImgIdx, panelRef])

  const renderImgPanel = () => {
    return imagesData.map((item: DemoImageData, index: number) => {
      return (
        <div className="dbr-img-div" key={index} style={{ border: generalStates.curImgIdx === index ? "2px solid #feb420" : "" }} onClick={() => { handleCurrentImage(index) }}>
          {
            isDeleteMode &&
            <div className="dbr-delete-mask">
              <svg width="32" height="32" viewBox="0 0 1792 1792" xmlns="http://www.w3.org/2000/svg"><path fill="#fff" d="M704 736v576q0 14-9 23t-23 9h-64q-14 0-23-9t-9-23v-576q0-14 9-23t23-9h64q14 0 23 9t9 23zm256 0v576q0 14-9 23t-23 9h-64q-14 0-23-9t-9-23v-576q0-14 9-23t23-9h64q14 0 23 9t9 23zm256 0v576q0 14-9 23t-23 9h-64q-14 0-23-9t-9-23v-576q0-14 9-23t23-9h64q14 0 23 9t9 23zm128 724v-948h-896v948q0 22 7 40.5t14.5 27 10.5 8.5h832q3 0 10.5-8.5t14.5-27 7-40.5zm-672-1076h448l-48-117q-7-9-17-11h-317q-10 2-17 11zm928 32v64q0 14-9 23t-23 9h-96v948q0 83-47 143.5t-113 60.5h-832q-66 0-113-58.5t-47-141.5v-952h-96q-14 0-23-9t-9-23v-64q0-14 9-23t23-9h309l70-167q15-37 54-63t79-26h320q40 0 79 26t54 63l70 167h309q14 0 23 9t9 23z"></path></svg>
            </div>
          }
          <img src={item.url ? item.url : require(`../../../../assets/image/${item.name}`)} alt="img" />
        </div>
      )
    });
  }

  return (
    <div className="dbr-viewer-footer" style={{ transform: `translateY(${isHideFooter ? "109px" : "0"})` }}>
      {/* The problem of width overflow element caused by container rotation, we will study the solution later */}
      {/* <div className="dbr-footer-toggle" onClick={() => { setBToggle(!bToggle) }} ref={toggleBtn} style={{top: bToggle ? "-11px" : "0"}}>
                {
                    bToggle ?
                        <svg className="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="2275" width="22" height="12"><path d="M534.6 403.5l294.2 294.2c12.5 12.5 32.8 12.5 45.3 0l0 0c12.5-12.5 12.5-32.8 0-45.3L557.3 335.6c-25-25-65.5-25-90.5 0L150 652.4c-12.5 12.5-12.5 32.8 0 45.3l0 0c12.5 12.5 32.8 12.5 45.3 0l294.1-294.2C501.9 391 522.1 391 534.6 403.5z" fill="#272636" p-id="2276"></path></svg>
                        :
                        <svg className="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="3248" width="22" height="12"><path d="M116.736 282.624c8.192 0 16.384 3.072 22.528 9.216l372.736 372.736L884.736 291.84c6.144-6.144 13.824-9.216 22.528-9.216 8.192 0 16.384 3.072 22.528 9.216 12.288 12.288 12.288 32.256 0 44.544L534.528 732.16c-6.144 6.144-13.824 9.216-22.528 9.216-8.192 0-16.384-3.072-22.528-9.216L94.208 336.896c-12.288-12.288-12.288-32.256 0-44.544 6.144-6.144 14.336-9.728 22.528-9.728z" fill="#2C2C2C" p-id="3249"></path></svg>
                }
            </div> */}
      <div className="dbr-img-panel" ref={panelRef}>{renderImgPanel()}</div>
      <div className="dbr-delete-icon">
        {
          isDeleteMode ?
            <svg onClick={() => { setDeleteMode(!isDeleteMode) }} className="icon" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg" p-id="7096" width="32" height="32"><path d="M672 352H236.8l105.6-105.6c12.8-12.8 12.8-32 0-44.8s-32-12.8-44.8 0l-160 160c-3.2 3.2-6.4 6.4-6.4 9.6-3.2 6.4-3.2 16 0 25.6 3.2 3.2 3.2 6.4 6.4 9.6l160 160c6.4 6.4 12.8 9.6 22.4 9.6s16-3.2 22.4-9.6c12.8-12.8 12.8-32 0-44.8L236.8 416H672c89.6 0 160 70.4 160 160v224c0 19.2 12.8 32 32 32s32-12.8 32-32v-224c0-124.8-99.2-224-224-224z" p-id="7097"></path></svg>
            :
            <svg onClick={() => { setDeleteMode(!isDeleteMode) }} width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path fill="#818e94" d="M4756.2,783h-10.4a2.57,2.57,0,0,1-2.5-2.25l-1.8-17.2a0.5,0.5,0,0,1,1-.1l1.8,17.2a1.535,1.535,0,0,0,1.5,1.352h10.4a1.535,1.535,0,0,0,1.5-1.35l1.8-17.2a0.5,0.5,0,0,1,1,.1l-1.8,17.2A2.569,2.569,0,0,1,4756.2,783Zm6.3-19h-23a0.5,0.5,0,0,1,0-1h23A0.5,0.5,0,0,1,4762.5,764Zm-7,0a0.5,0.5,0,0,1-.5-0.5v-3a0.452,0.452,0,0,0-.5-0.5h-7a0.452,0.452,0,0,0-.5.5v3a0.5,0.5,0,0,1-1,0v-3a1.437,1.437,0,0,1,1.5-1.5h7a1.45,1.45,0,0,1,1.5,1.5v3A0.5,0.5,0,0,1,4755.5,764Zm-4.5,15a0.5,0.5,0,0,1-.5-0.5V768a0.5,0.5,0,0,1,1,0v10.5A0.5,0.5,0,0,1,4751,779Zm4,0h-0.02a0.5,0.5,0,0,1-.48-0.522l0.5-10.5a0.5,0.5,0,0,1,1,.048l-0.5,10.5A0.5,0.5,0,0,1,4755,779Zm-8,0a0.5,0.5,0,0,1-.5-0.477l-0.5-10.5a0.5,0.5,0,0,1,.48-0.523,0.488,0.488,0,0,1,.52.475l0.5,10.5a0.5,0.5,0,0,1-.48.522H4747Z" transform="translate(-4739 -759)"></path></svg>
        }
      </div>
    </div>
  );
}
